/**
 * How a workflow edge looks — the ONE place (AUT-F0-T1).
 *
 * Before this file an edge's look was decided in four places that disagreed:
 * onConnect and <ReactFlow defaultEdgeOptions> drew a bezier with an inline
 * stroke colour built on a token the theme does not define (a bare
 * --muted-foreground; the theme defines --color-muted-foreground, in oklch),
 * so a drawn edge computed no stroke at all and was invisible; the two load
 * adapters drew a smoothstep with no colour, so the same edge changed shape
 * after a reload.
 *
 * Now:
 *   - EDGE_DEFAULTS is the shape every edge object takes — drawn
 *     (newEdgeFromConnection), loaded (serverEdgesToRF), pasted and React
 *     Flow's defaultEdgeOptions. It carries NO style: no edge object anywhere
 *     sets a stroke colour or width inline, so React Flow's own `.selected`
 *     rule applies.
 *   - CANVAS_THEME_STYLE sets the colours once, on <ReactFlow style>, through
 *     React Flow 12's own CSS variables, valued from the theme tokens in
 *     src/index.css. `.dark` redefines --color-*, so light and dark follow
 *     without a branch here.
 *   - isDuplicateConnection is the canvas's one duplicate rule.
 */

import type { CSSProperties } from 'react';
import type { Connection, Edge } from '@xyflow/react';

/**
 * The edge shape. `smoothstep` is what every saved workflow has rendered as
 * since b91, so no existing workflow changes shape. A newly drawn edge takes
 * `animated` from here; a loaded edge keeps its stored value.
 */
export const EDGE_DEFAULTS = {
  type: 'smoothstep',
  animated: false,
  labelStyle: { fontSize: 10 },
} as const satisfies Pick<Edge, 'type' | 'animated' | 'labelStyle'>;

/** A fresh copy of the defaults (no edge shares the labelStyle object). */
export function edgeDefaults(): { type: string; animated: boolean; labelStyle: CSSProperties } {
  return {
    type: EDGE_DEFAULTS.type,
    animated: EDGE_DEFAULTS.animated,
    labelStyle: { ...EDGE_DEFAULTS.labelStyle },
  };
}

/** The edge onConnect adds for a connection the person drew. */
export function newEdgeFromConnection(connection: Connection, id: string): Edge {
  return {
    id,
    source: connection.source,
    target: connection.target,
    sourceHandle: connection.sourceHandle ?? null,
    targetHandle: connection.targetHandle ?? null,
    ...edgeDefaults(),
  };
}

/**
 * The canvas colours, as React Flow 12.10's CSS variables valued from theme
 * tokens. Set on <ReactFlow style>; the in-progress connection line and a
 * selected edge use the theme's primary colour, a resting edge the muted
 * foreground, and the pane paints the theme background (React Flow's dark
 * colour mode would otherwise paint its own #141414).
 */
export const CANVAS_THEME_STYLE = {
  '--xy-edge-stroke': 'color-mix(in oklab, var(--color-muted-foreground) 60%, transparent)',
  '--xy-edge-stroke-selected': 'var(--color-primary)',
  '--xy-edge-stroke-width': '1.5',
  '--xy-connectionline-stroke': 'var(--color-primary)',
  '--xy-connectionline-stroke-width': '1.5',
  '--xy-background-color': 'var(--color-background)',
} as CSSProperties;

/** The dot-grid colour for <Background color>, from the theme. */
export const CANVAS_GRID_COLOR = 'color-mix(in oklab, var(--color-muted-foreground) 30%, transparent)';

/**
 * The id of a node's first handle of a kind, or null when it is not known.
 * A stored edge with a NULL handle is drawn on exactly that handle.
 */
export type FirstHandleOf = (nodeId: string, kind: 'source' | 'target') => string | null;

/**
 * The canvas's one duplicate rule: a connection is a duplicate when an edge
 * already leaves the same source node from the same source handle for the
 * same target node — WHATEVER target handle either one ends on. The engine
 * reads a node's upstreams by node id (automate-runner.service.ts execMerge),
 * so a second edge into another input of the same node would only read the
 * same output twice. A NULL source handle (an edge saved before handles were
 * kept, or seeded without one) is compared as the node's first source handle.
 * A different source handle (a condition's true and false) is a different
 * connection and is allowed.
 */
export function isDuplicateConnection(
  edges: ReadonlyArray<Pick<Edge, 'source' | 'target' | 'sourceHandle'>>,
  connection: Pick<Edge, 'source' | 'target' | 'sourceHandle'>,
  firstHandleOf: FirstHandleOf,
): boolean {
  const sourceHandle = (nodeId: string, handle: string | null | undefined) =>
    handle ?? firstHandleOf(nodeId, 'source');
  const wanted = sourceHandle(connection.source, connection.sourceHandle);
  return edges.some(
    (e) =>
      e.source === connection.source &&
      e.target === connection.target &&
      sourceHandle(e.source, e.sourceHandle) === wanted,
  );
}
